import { Injectable } from '@angular/core';
import { LdsUserService } from '../services/lds-user.service';
import { EnergyTotalByCategory, FeatureSetData, FeatureSetsData, TimeSeriesData } from '../interfaces/data-analytics';
import { StorageMap } from '@ngx-pwa/local-storage';
import { LdsEventBusService } from '../services/lds-event-bus.service';
import { time } from '../services/global-variables.service';
import { DemoAccountService } from '../demo-account.service';

@Injectable({
  providedIn: 'root'
})

export class EnergyDataService {
  _dailyData: any;
  _featureSetsWithData: FeatureSetData[] = [];
  _featureSetsWithoutData: FeatureSetData[] = [];
  timeNow = Date.now();
  private count = 0;
  private roomByFeatureSetId: Map<string, string> | null = null;

  constructor(private user: LdsUserService, protected localStorage: StorageMap,
      private eventBus: LdsEventBusService, private demo: DemoAccountService) {
    this.count = 0;
  }

  /**
   * Build room index for O(1) lookups instead of O(n*m) nested loops
   */
  private buildRoomIndex(): void {
    if (this.roomByFeatureSetId) return; // Already built
    
    this.roomByFeatureSetId = new Map();
    for (const room of this.user.rooms) {
      for (const featureSetId of room.featureSets) {
        this.roomByFeatureSetId.set(featureSetId, room.name);
      }
    }
  }

  /**
   * Invalidate room index cache when rooms change
   */
  invalidateRoomIndex(): void {
    this.roomByFeatureSetId = null;
  }

  async calculateTotal(data) {
    let noData = data.length ? false : true;
    let total = noData ? 0 : data.map((a) => a.value).reduce((a, b) => a + b);
    let last_updated = noData ? null : data[data.length - 1].timestamp;
    return {
      total: (total / 1000).toFixed(1),
      noData: noData,
      last_updated: last_updated
    };
  }

  checkMoreThanZero = (data) => {
    const moreThanZero = data.find(element => element.value > 0);
    if (moreThanZero) {
      return data;
    } else {
      data = data.map(element => {
        element.value = null;
        return element;
      });
    }
    return data;
  }

  async addMonthlyData(energyFeatureSet, data) {
    this.count += 1;

    // if (this.user.isEnergyMonitor(energyFeatureSet)) {
    //   this.user.addEnergyMonitor(energyFeatureSet);
    // }

    if (!this.user.isEnergyMonitor(energyFeatureSet)) {
      // if (!this.demo.isDemo()) {
      this._featureSetsWithData.push(energyFeatureSet);
    }

    let item = await this.calculateTotal(data);

    energyFeatureSet.monthly_data = [];
    if (item.noData === false) {
      this.checkMoreThanZero(data);
      energyFeatureSet.monthly_data = data;
      energyFeatureSet.monthly_total = item.total;
      if (!this.user.isEnergyMonitor(energyFeatureSet)) {
        this.user._totalMonthlyUsage = this.user._totalMonthlyUsage + parseFloat(item.total);
      }
      
      this.eventBus.broadcast(energyFeatureSet.groupId, energyFeatureSet);
    }


    this.eventBus.broadcast('energy-featureSet-loaded', data);

    
    // if (this.count >= this.user.energyFeatureSetsCount) {
    if (this.count >= this.user.energySummaryFeatureSetsCount) {
      let savePacket = {
        expires: this.timeNow + time.hour,
        featureSetsWithData: this._featureSetsWithData,
        featureSetsWithoutData: this._featureSetsWithoutData,
        features: this.user.features,
        energyFeatureSets: this.user.energyFeatureSets,
        energyMonitors: this.user.energyMonitors,
        motionFeatureSets: this.user.motionFeatureSets,
        allDevices: this.user.allDevices,
        rooms: this.user.rooms,
        homes: this.user.homes,
        deviceTypes: this.user.deviceTypes,
        temperatureFeatureSets: this.user.temperatureFeatureSets,
        switchFeatureSets: this.user.switchFeatureSets
      };


      this.localStorage.set(this.user.userDetail.user._id + '-lds-energy-featureSets', savePacket).subscribe(() => {
        this.eventBus.broadcast('energy-featureSets-loaded', true);
      });
      this.count = 0;
    }
  }

  /**
   * Process monthly data and derive weekly data from it.
   * This reduces HTTP calls from 2 per featureSet to 1.
   */
  async addMonthlyDataAndDeriveWeekly(energyFeatureSet: FeatureSetData, monthlyData: any[]) {
    // Derive weekly data from the last 7 days of monthly data
    const weeklyData = this.deriveWeeklyFromMonthly(monthlyData);
    
    // Process weekly (daily) data
    await this.addDailyData(energyFeatureSet, weeklyData);
    
    // Process monthly data
    await this.addMonthlyData(energyFeatureSet, monthlyData);
  }

  /**
   * Extract the last 7 days of data from monthly dataset
   */
  private deriveWeeklyFromMonthly(monthlyData: any[]): any[] {
    if (!monthlyData || monthlyData.length === 0) {
      return [];
    }

    // Sort by timestamp descending to get most recent first
    const sortedData = [...monthlyData].sort((a, b) => b.timestamp - a.timestamp);
    
    // Take the last 7 entries (most recent 7 days)
    const weeklyData = sortedData.slice(0, 7);
    
    // Return in ascending order (oldest first) to match original format
    return weeklyData.reverse();
  }

  async addDailyData(energyFeatureSet: FeatureSetData, data) {
    let item = await this.calculateTotal(data);
    if (item.noData) {
      energyFeatureSet.total = item.total;
      energyFeatureSet.daily_data = [];
      this._featureSetsWithoutData.push(energyFeatureSet);
    } else {
      this.checkMoreThanZero(data);
      energyFeatureSet.last_updated = item.last_updated;
      if (item.last_updated > this.timeNow - time.day) {
        energyFeatureSet.up_to_date = true;
      } else {
        energyFeatureSet.out_of_date = true;
      }
      energyFeatureSet.daily_data = data;
      energyFeatureSet.total = item.total;
      if (!this.user.isEnergyMonitor(energyFeatureSet)) {
        this.user._totalWeeklyUsage = this.user._totalWeeklyUsage + parseFloat(item.total);
      }
    }
    
    return energyFeatureSet;
  }

  getSelectedFeatureSets() {
    let local = [];
    for (let index in this._featureSetsWithData) {
      if (!this._featureSetsWithData[index].selected) {
        continue;
      }
      local.push(this.featureSetsWithData[index]);
    }
    return local;
  }

  async calculateEnergyByCategory(selected: FeatureSetsData): Promise<EnergyTotalByCategory> {
    let local = [];
    return new Promise<EnergyTotalByCategory>(resolve => {
      for (let index = 0; index < selected.length; index++) {
        const element = selected[index];
        if (!element.selected) {
          continue;
        }
        const deviceId = element.deviceId;
        const deviceType = element.type;
        let room = this.findRoomName(element.groupId);
        if (Math.abs(+element.total)) {
          local.push({
            label: element.name,
            value: +element.total,
            deviceId: deviceId,
            deviceType: deviceType,
            groupId: element.groupId,
            room: room
          });
        }
      }
      resolve(local);
    });
  }

  findRoomName(groupId: string): string {
    // Use cached Map for O(1) lookup instead of O(n*m) nested loops
    this.buildRoomIndex();
    return this.roomByFeatureSetId?.get(groupId) ?? '';
  }


  async calculateMonthlyEnergyByCategory(selected: FeatureSetsData): Promise<EnergyTotalByCategory> {
    let local = [];
    return new Promise<EnergyTotalByCategory>(resolve => {
      for (let index = 0; index < selected.length; index++) {
        const element = selected[index];
        if (!element.selected) {
          continue;
        }
        const deviceId = element.deviceId;
        const deviceType = element.type;
        let room = '';
        if (deviceType != null) {
          for (let roomId in this.user.rooms) {
            const r = this.user.rooms[roomId];
            for (let featureSetId in r.featureSets) {
              if (r.featureSets[featureSetId] == element.groupId) {
                room = r.name;
                break;
              }
            }
          }
        }
        if (Math.abs(+element.monthly_total)) {
          local.push({
            label: element.name,
            value: +element.monthly_total,
            deviceId: deviceId,
            deviceType: deviceType,
            groupId: element.groupId,
            room: room
          });
        }
      }
      resolve(local);
    });
  }

  public aggregateEnergyDailyData(data: TimeSeriesData, aggregated) {
    for (let index = 0; index < data.length; index++) {
      const element = data[index];
      const date = new Date(element.timestamp);
      date.setHours(0, 0, 0, 0);
      element.timestamp = date.getTime();
      if (element.timestamp in aggregated) {
        aggregated[element.timestamp] += element.value;
      } else {
        aggregated[element.timestamp] = element.value;
      }
    }
    return aggregated;
  }


  async calculateTotalEnergyByDate(selected: FeatureSetsData): Promise<any> {
    return new Promise(async resolve => {
      const local = [];

      for (let i in selected) {
        let featureSet = selected[i];
        if (local.length === 0) {
          for (let j in featureSet.daily_data) {
            let data = featureSet.daily_data[j];
            local.push({
              timestamp: data.timestamp,
              value: data.value
            });
          }
        } else {
          for (let j in featureSet.daily_data) {
            if (!local[j]) {
              continue;
            }
            local[j].value = local[j].value + featureSet.daily_data[j].value;
          }
        }
      }
      for (const key in local) {
        if (local.hasOwnProperty(key)) {
          local[key].value = Number((local[key].value / 1000).toFixed(1))
        }
      }
      resolve(local);
    });

  }

  async calculateTotalMonthlyEnergyByDate(selected: FeatureSetsData): Promise<any> {
    return new Promise(async resolve => {
      const local = [];

      for (let i in selected) {
        let featureSet = selected[i];
        if (local.length === 0) {
          for (let j in featureSet.monthly_data) {
            let data = featureSet.monthly_data[j];
            local.push({
              timestamp: data.timestamp,
              value: data.value
            });
          }
        } else {
          for (let j in featureSet.monthly_data) {
            if (!local[j]) {
              continue;
            }
            local[j].value = local[j].value + featureSet.monthly_data[j].value;
          }
        }
      }
      for (const key in local) {
        if (local.hasOwnProperty(key)) {
          local[key].value = Number((local[key].value / 1000).toFixed(1))
        }
      }
      resolve(local);
    });
  }

  get featureSetsWithData() {
    return this._featureSetsWithData;
  }

  get featureSetsWithoutData() {
    return this._featureSetsWithoutData;
  }

  set featureSetsWithData(data) {
    this._featureSetsWithData = data;
    if (data.length) {
      this.eventBus.broadcast('energy-featureSets-loaded-from-storage');
      this.eventBus.broadcast('energy-featureSets-loaded', true);
      this.eventBus.broadcast('energySummary-featureSets-loaded', true);
    }
  }

  set featureSetsWithoutData(data) {
    this._featureSetsWithoutData = data;
  }

  initiate() {
    this.featureSetsWithData = [];
    this.featureSetsWithoutData = [];
  }
}
